import { z } from "zod";
import { calculationLotSchema, taxIssueCodeSchema } from "./calculation.js";
import { cursorPageSchema } from "./context.js";
/**
 * Read-only audit view over every calculation job an organization ran: durable
 * fiscal tasks, ad-hoc or integration calculations grouped by caller and
 * correlation, and imported lot uploads that were never run. Nothing here is
 * stored; each row is projected from immutable records at read time.
 */
export const CALCULATION_JOB_DEFAULT_WINDOW_DAYS = 30;
export const CALCULATION_JOB_MAX_WINDOW_DAYS = 90;
export const CALCULATION_JOB_MAX_LIMIT = 100;
export const CALCULATION_JOB_MAX_RULESET_VERSIONS = 5;
export const CALCULATION_JOB_MAX_CALCULATION_IDS = 50;
export const calculationJobKindSchema = z.enum(["FISCAL_TASK", "CALCULATION_GROUP", "LOT_UPLOAD"]);
/**
 * Where the job came from. UNKNOWN is a legacy calculation whose caller was
 * never recorded; it is never inferred from payload contents.
 */
export const calculationJobOriginSchema = z.enum([
    "LOT_UPLOAD",
    "FISCAL_TASK",
    "CALCULATOR",
    "INTEGRATION",
    "DYNAMIC_LIST",
    "RUNTIME",
    "UNKNOWN",
]);
export const calculationJobStatusSchema = z.enum([
    "IMPORTED",
    "INVALID",
    "QUEUED",
    "RUNNING",
    "COMPLETED",
    "REVIEW_REQUIRED",
    "PARTIAL",
    "FAILED",
    "CANCELLED",
]);
export const calculationJobSubmitterKindSchema = z.enum([
    "OPERATOR",
    "INTEGRATION",
    "MACHINE",
    "UNKNOWN",
]);
export const calculationJobSortSchema = z.enum([
    "submittedAt",
    "completedAt",
    "items",
    "reviewRequired",
]);
const moneySchema = z.string().regex(/^(0|[1-9]\d{0,15})(\.\d{1,2})?$/);
const countSchema = z.number().int().nonnegative();
const UPLOAD_STATUSES = new Set(["IMPORTED", "INVALID"]);
export const calculationJobSchema = z
    .object({
    kind: calculationJobKindSchema,
    /** Fiscal task ID, upload ID, or the first calculation ID of a group. */
    jobId: z.string().uuid(),
    origin: calculationJobOriginSchema,
    status: calculationJobStatusSchema,
    /** The stored status the job status was derived from, for audit. */
    sourceStatus: z.string().min(1).max(40),
    workflowId: z.string().min(1).max(100),
    correlationId: z.string().min(1).max(128).nullable(),
    lot: calculationLotSchema.nullable(),
    submittedBy: z
        .object({
        kind: calculationJobSubmitterKindSchema,
        id: z.string().min(1).max(200).nullable(),
        /** Display name or email of a principal, or the integration name. */
        name: z.string().min(1).max(320).nullable(),
    })
        .strict(),
    submittedAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    rulesetVersions: z.array(z.string().min(1).max(80)).max(CALCULATION_JOB_MAX_RULESET_VERSIONS),
    counts: z
        .object({
        items: countSchema,
        calculated: countSchema,
        reviewRequired: countSchema,
        rejected: countSchema,
        invalidRows: countSchema.nullable(),
        batches: z
            .object({ received: countSchema, expected: z.number().int().positive().nullable() })
            .strict(),
    })
        .strict(),
    issueCounts: z.partialRecord(taxIssueCodeSchema, countSchema),
    /**
     * Quantity-weighted sums over usable item results only (CALCULATED, authoritative, no finance
     * review), the same definition as the lot analysis (`lotAnalysisSchema.totals`). Review rows
     * never count. `landedUnitCost` is Σ landed unit cost × quantity. Null while running or when no
     * row is usable.
     */
    totals: z
        .object({
        taxTotal: moneySchema,
        landedUnitCost: moneySchema,
        currency: z.literal("BRL"),
    })
        .strict()
        .nullable(),
    /** Quantity-weighted usable tax (equals `totals.taxTotal`); optional for older API responses. */
    quantityTaxTotal: moneySchema.nullable().optional(),
    calculationIds: z.array(z.string().uuid()).max(CALCULATION_JOB_MAX_CALCULATION_IDS),
    calculationCount: countSchema,
    uploadId: z.string().uuid().nullable(),
    integration: z
        .object({ integrationId: z.string().uuid(), name: z.string().min(1).max(120) })
        .strict()
        .nullable(),
    lastError: z
        .object({ code: z.string().min(1).max(80), message: z.string().min(1).max(1_000) })
        .strict()
        .nullable(),
})
    .strict()
    .superRefine((job, context) => {
    const uploadStatus = UPLOAD_STATUSES.has(job.status);
    if ((job.kind === "LOT_UPLOAD") !== uploadStatus) {
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "IMPORTED and INVALID are reserved for unrun lot uploads",
        });
    }
    if (job.kind === "LOT_UPLOAD" && (job.uploadId !== job.jobId || job.origin !== "LOT_UPLOAD")) {
        context.addIssue({
            code: "custom",
            path: ["uploadId"],
            message: "An unrun upload is identified by its upload ID and LOT_UPLOAD origin",
        });
    }
    if (job.status === "PARTIAL" && job.kind !== "CALCULATION_GROUP") {
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "PARTIAL is reserved for calculation groups missing batches",
        });
    }
    if (job.calculationIds.length > job.calculationCount) {
        context.addIssue({
            code: "custom",
            path: ["calculationIds"],
            message: "calculationIds cannot exceed calculationCount",
        });
    }
    if (job.integration !== null && job.origin !== "INTEGRATION") {
        context.addIssue({
            code: "custom",
            path: ["integration"],
            message: "integration is only present when the origin is INTEGRATION",
        });
    }
});
export const calculationJobPageSchema = cursorPageSchema
    .extend({
    items: z.array(calculationJobSchema).max(CALCULATION_JOB_MAX_LIMIT),
    totalEstimate: countSchema,
})
    .strict();
/** Whole-window mission control aggregate, independent of the list page. */
export const calculationJobSummarySchema = z
    .object({
    lots: countSchema,
    calculated: countSchema,
    review: countSchema,
    tax: moneySchema.nullable(),
})
    .strict();
//# sourceMappingURL=calculation-jobs.js.map